import { Component, Input, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';

import { Workitem } from 'common/xislivechat/models/workitem';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { WorkItemEventParams, WorkItemEventValues, WorkItemEvents } from 'common/common/constants/analytics-enums';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { UserContextService } from 'common/user-context/user-context.service';
import { TextChatMessagesService } from 'common/text-chat-messages/text-chat-messages.service';
import { BcCannedMessagesRepo } from 'common/canned-messages/canned-messages-repo';
import { BcAnalytics } from 'common/analytics/analytics.service';


import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { WorkItemSms } from 'common/workitems/models/work-item-sms';
import { WorkItemFacebook } from 'common/workitems/models/work-item-facebook';
import { WorkItemAsyncMessage } from 'common/workitems/models/work-item-async-message';
import { BcRestApi } from 'common/api/rest-api';
import { PersonType } from 'common/common/constants/person-type.enum';
import { taskItemAnimation } from '../../shared/animations/task-item.animation';

@Component({
  selector: 'workitem-text-chat-list-content',
  templateUrl: './workitem-text-chat-list-content.component.html',
  styleUrls: ['./workitem-text-chat-list-content.scss'],
  animations: [taskItemAnimation],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemTextChatListContentComponent {
  @Input() textChatWorkItemList: Workitem[] = [];
  @Input() visitorLanguageCode: string;

  constructor(public workItemListService: WorkItemListService,
              public userContextService: UserContextService,
              private _textChatMessagesService: TextChatMessagesService,
              private _bcRestApi: BcRestApi,
              private _cannedMessagesRepo: BcCannedMessagesRepo,
              private _bcAnalytics: BcAnalytics,
              private _changeDetector: ChangeDetectorRef) {
  }

  public getTextChatWorkitemCount() {
    return this.textChatWorkItemList.length;
  }

  public trackByWorkItemId(index, workItem): string {
    return workItem.id;
  }

  public onWorkItemKeydown(event, workitem) {
    if (event.which === KeyboardKeyCodes.ENTER) {
      event.preventDefault();
      event.stopPropagation();

      if (event.shiftKey) {
        if (!workitem.IsAnswered) {
          this.acceptButtonClicked(workitem, event);
        }
      } else {
        this.selectWorkItem(workitem);
      }

      this._changeDetector.detectChanges();
    }
  }

  public acceptButtonClicked(workItem: WorkItemSms | WorkItemFacebook | WorkItemAsyncMessage, event) {
    if (!workItem.IsAnswered) {
      this._bcAnalytics.eventTrack(WorkItemEvents.WORK_ITEM_ACCEPTED, {
        [WorkItemEventParams.FROM]: WorkItemEventValues.BUTTON,
        [WorkItemEventParams.TYPE]: this._getWorkItemType(workItem),
        [WorkItemEventParams.VISITOR_LANGUAGE]: this.visitorLanguageCode,
        [WorkItemEventParams.QUICK_ACCEPT]: true
      });

      return this._acceptWorkItem(workItem)
        .then(() => {
          return this._cannedMessagesRepo.getTextChatWelcomeCannedMessage(workItem.id)
            .then((cannedMessage) => {
              if (!cannedMessage) {
                return Promise.resolve();
              }

              return this._textChatMessagesService.sendTextChatMessage({
                TextChatID: workItem.id,
                Name: this.userContextService.userContext.name,
                Text: cannedMessage.text
              });
            });
        });
    }

    event.stopPropagation();
    event.preventDefault();
  }

  public isWorkItemAcceptable(workItem): boolean {
    return (workItem.isSms() || workItem.isFacebook() || workItem.isAsyncMessage()) && !workItem.IsAnswered;
  }

  public selectWorkItem(workItem) {
    switch (this._getWorkItemType(workItem)) {
      case WorkItemTypes.SMS:
        this.workItemListService.selectAndAcceptSmsWorkItem(workItem);
        break;
      case WorkItemTypes.FACEBOOK:
        this.workItemListService.selectAndAcceptFacebookWorkItem(workItem);
        break;
      case WorkItemTypes.ASYNC_MESSAGE:
        this.workItemListService.selectAndAcceptAsyncMessageWorkItem(workItem);
        break;
    }
  }

  public generateWorkitemClass(workItem): string {
    return this.workItemListService.generateWorkitemClass(workItem);
  }

  public generateLastMessagesClass(lastMessage) {
    let classStr = 'message';
    if (lastMessage.PersonType === PersonType.Operator) {
      classStr += ' sent';
    }
    if (lastMessage.PersonType === PersonType.Visitor) {
      classStr += ' received';
    }
    return classStr;
  }

  private _acceptWorkItem(workitem) {
    workitem.IsAnswered = true; //hide quick accept button immediately

    return this._bcRestApi.answerTextChat(workitem.id)
      .then(() => {
        this._bcAnalytics.eventTrack('WorkItemAnswered', { type: this._getWorkItemType(workitem) });
        this.workItemListService.stopTimerForItem(workitem);
      });
  }

  private _getWorkItemType(workItem) {
    if (workItem.isSms()) {
      return WorkItemTypes.SMS;
    } else if (workItem.isFacebook()) {
      return WorkItemTypes.FACEBOOK;
    } else if (workItem.isAsyncMessage()) {
      return WorkItemTypes.ASYNC_MESSAGE;
    } else {
      throw new Error('Please specify the correct work item type.');
    }
  }
}
